import React, {
  createContext,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Menu.module.css';

// ─── Context ────────────────────────────────────────────────────────────────

interface MenuContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  triggerId: string;
  contentId: string;
  triggerRef: React.RefObject<HTMLElement>;
  activeIndex: number;
  setActiveIndex: (i: number) => void;
  registerItem: (el: HTMLElement) => () => void;
}

const MenuContext = createContext<MenuContextValue | null>(null);

function useMenu() {
  const ctx = useContext(MenuContext);
  if (!ctx) throw new Error('Menu sub-components must be used inside <Menu>');
  return ctx;
}

// ─── Menu (root) ─────────────────────────────────────────────────────────────

export interface MenuProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function Menu({ children, defaultOpen = false, open: controlledOpen, onOpenChange }: MenuProps) {
  const uid = useId();
  const triggerId = `menu-trigger-${uid}`;
  const contentId = `menu-content-${uid}`;
  const triggerRef = useRef<HTMLElement>(null);
  const itemsRef = useRef<HTMLElement[]>([]);
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [activeIndex, setActiveIndex] = useState(-1);

  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen! : internalOpen;

  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
    if (!next) setActiveIndex(-1);
  };

  const registerItem = (el: HTMLElement) => {
    itemsRef.current = [...itemsRef.current, el];
    return () => {
      itemsRef.current = itemsRef.current.filter((i) => i !== el);
    };
  };

  return (
    <MenuContext.Provider
      value={{ open, setOpen, triggerId, contentId, triggerRef, activeIndex, setActiveIndex, registerItem }}
    >
      <div className={styles.root}>{children}</div>
    </MenuContext.Provider>
  );
}

Menu.displayName = 'Menu';

// ─── MenuTrigger ─────────────────────────────────────────────────────────────

export interface MenuTriggerProps {
  children: React.ReactElement;
  asChild?: boolean;
}

function MenuTrigger({ children }: MenuTriggerProps) {
  const { open, setOpen, triggerId, contentId, triggerRef } = useMenu();

  const child = React.Children.only(children);

  return React.cloneElement(child, {
    id: triggerId,
    ref: triggerRef,
    'aria-haspopup': 'menu',
    'aria-expanded': open,
    'aria-controls': open ? contentId : undefined,
    onClick: (e: React.MouseEvent) => {
      child.props.onClick?.(e);
      setOpen(!open);
    },
  } as React.HTMLAttributes<HTMLElement>);
}

MenuTrigger.displayName = 'MenuTrigger';

// ─── MenuContent ─────────────────────────────────────────────────────────────

export interface MenuContentProps {
  children: React.ReactNode;
  align?: 'start' | 'end' | 'center';
  side?: 'bottom' | 'top';
  className?: string;
}

function MenuContent({
  children,
  align = 'start',
  side = 'bottom',
  className,
}: MenuContentProps) {
  const { open, setOpen, triggerId, contentId, triggerRef, activeIndex, setActiveIndex } = useMenu();
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ visibility: 'hidden' });

  // Position the panel relative to the trigger
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !panelRef.current) return;

    const update = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const panelH = panelRef.current!.offsetHeight;
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      let top: number;
      let left: number;

      if (side === 'bottom') {
        top = rect.bottom + scrollY + 6;
      } else {
        top = rect.top + scrollY - panelH - 6;
      }

      if (align === 'start') {
        left = rect.left + scrollX;
      } else if (align === 'end') {
        left = rect.right + scrollX;
      } else {
        left = rect.left + scrollX + rect.width / 2;
      }

      setStyle({ top, left, visibility: 'visible' });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, side, align, triggerRef]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        panelRef.current?.contains(e.target as Node) ||
        triggerRef.current?.contains(e.target as Node)
      )
        return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, setOpen, triggerRef]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      const items = panelRef.current?.querySelectorAll<HTMLElement>(
        '[role="menuitem"]:not([aria-disabled="true"])',
      );
      if (!items?.length) return;
      const arr = Array.from(items);

      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = activeIndex < arr.length - 1 ? activeIndex + 1 : 0;
        setActiveIndex(next);
        arr[next]?.focus();
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = activeIndex > 0 ? activeIndex - 1 : arr.length - 1;
        setActiveIndex(prev);
        arr[prev]?.focus();
        return;
      }
      if (e.key === 'Tab') {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, activeIndex, setActiveIndex, setOpen, triggerRef]);

  if (!open) return null;

  const alignClass = align === 'end' ? styles.alignEnd : align === 'center' ? styles.alignCenter : '';
  const sideClass = side === 'top' ? styles.sideTop : '';

  return createPortal(
    <div
      ref={panelRef}
      id={contentId}
      role="menu"
      aria-labelledby={triggerId}
      className={[styles.content, alignClass, sideClass, className].filter(Boolean).join(' ')}
      style={style}
    >
      {children}
    </div>,
    document.body,
  );
}

MenuContent.displayName = 'MenuContent';

// ─── MenuItem ────────────────────────────────────────────────────────────────

export type MenuItemVariant = 'default' | 'destructive';

export interface MenuItemProps extends React.ComponentPropsWithoutRef<'button'> {
  icon?: React.ReactNode;
  endIcon?: React.ReactNode;
  variant?: MenuItemVariant;
  shortcut?: string;
}

function MenuItem({
  icon,
  endIcon,
  variant = 'default',
  shortcut,
  disabled,
  className,
  children,
  onClick,
  ...rest
}: MenuItemProps) {
  const { setOpen } = useMenu();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    onClick?.(e);
    setOpen(false);
  };

  const classes = [
    styles.item,
    variant === 'destructive' && styles.destructive,
    disabled && styles.disabled,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button
      role="menuitem"
      tabIndex={-1}
      className={classes}
      disabled={disabled}
      aria-disabled={disabled}
      onClick={handleClick}
      {...rest}
    >
      {icon && <span className={styles.itemIcon} aria-hidden>{icon}</span>}
      <span className={styles.itemLabel}>{children}</span>
      {shortcut && <kbd className={styles.shortcut}>{shortcut}</kbd>}
      {endIcon && !shortcut && <span className={styles.itemIcon} aria-hidden>{endIcon}</span>}
    </button>
  );
}

MenuItem.displayName = 'MenuItem';

// ─── MenuSeparator ───────────────────────────────────────────────────────────

export interface MenuSeparatorProps {
  className?: string;
}

function MenuSeparator({ className }: MenuSeparatorProps) {
  return <div role="separator" className={[styles.separator, className].filter(Boolean).join(' ')} />;
}

MenuSeparator.displayName = 'MenuSeparator';

// ─── MenuLabel ───────────────────────────────────────────────────────────────

export interface MenuLabelProps {
  children: React.ReactNode;
  className?: string;
}

function MenuLabel({ children, className }: MenuLabelProps) {
  return (
    <div className={[styles.label, className].filter(Boolean).join(' ')} aria-hidden>
      {children}
    </div>
  );
}

MenuLabel.displayName = 'MenuLabel';

// ─── Exports ─────────────────────────────────────────────────────────────────

export { Menu, MenuTrigger, MenuContent, MenuItem, MenuSeparator, MenuLabel };
